/**
 * App Store — Zustand store for global UI state
 *
 * Only UI state lives here. 3D/scene state lives in the Engine.
 * Frontend reads engine state via events, never writes to it directly.
 *
 * @see docs/ims-brand-frontends/04-state-management.md
 */
import { create } from 'zustand';
import { loadConsent, saveConsent, clearConsent } from '../utils/consentStorage';

// Hydrate consent synchronously before any React render so the banner
// is never shown to users who already made a choice.
const _savedConsent = loadConsent();

// Detect embed mode from URL: ?mode=embed
const _urlParams = new URLSearchParams(window.location.search);
const _mode = _urlParams.get('mode');
const _isEmbed = _mode === 'embed';
const _isKiosk = _mode === 'kiosk';
// Embed controls level: 'none' (default) | 'minimal' (color swatches) | 'full' (full menu bar)
const _embedControls = (_urlParams.get('controls') ?? 'none') as 'none' | 'minimal' | 'full';
// Auto-AR: ?ar=auto — skip carousel so the scene loads immediately, then AR triggers.
const _isArAuto = _urlParams.get('ar') === 'auto';
// Initial scene override: ?scene=transparent — embed-only, transparent canvas background.
const _initialScene = _urlParams.get('scene');
const _isTransparentBg = _isEmbed && _initialScene === 'transparent';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AppScreen = 'qr' | 'start' | 'loading' | 'viewer';
export type CameraMode = 'exterior' | 'interior';

/**
 * ActiveSubMenu — mutually exclusive UI panel slots.
 *
 * Panel submenus (slide up above MenuBar):   'color' | 'rim' | 'scene' | 'animation'
 * Overlay submenus (fullscreen / sheet):     'info' | 'ar' | 'cookie' | 'license'
 *
 * MenuBar reads this to highlight the active button.
 * SubMenuPanel reads this to decide what content to show.
 */
export type ActiveSubMenu =
  | null
  | 'color'
  | 'rim'
  | 'scene'
  | 'animation'
  | 'info'
  | 'ar'
  | 'cookie'
  | 'license';

/** Panel-type submenus — these cause the SubMenuPanel to slide up. */
export const PANEL_SUBMENUS: ReadonlySet<ActiveSubMenu> = new Set([
  'color',
  'rim',
  'scene',
  'animation',
]);

export interface AppError {
  code: string;
  recoverable: boolean;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface AppState {
  // --- Screen ---
  screen: AppScreen;

  // --- Embed mode ---
  /** True when ?mode=embed — skips carousel, cookie banner, footer, CTA. */
  embedMode: boolean;
  /** Embed controls level: 'none' | 'minimal' (colors) | 'full' (menu bar). Only relevant when embedMode=true. */
  embedControls: 'none' | 'minimal' | 'full';
  /** True when ?ar=auto — skips carousel, loads scene, then auto-opens AR on mobile. */
  arAutoMode: boolean;
  /** True when ?mode=kiosk — dealer kiosk: no cookie banner, no footer, no CTAs, auto-reset on inactivity. */
  kioskMode: boolean;
  /** True when the canvas background should be transparent (embed only). Set via ?scene=transparent or ims:setScene. */
  transparentBg: boolean;

  // --- Camera ---
  cameraMode: CameraMode;

  // --- UI panels (mutually exclusive) ---
  activeSubMenu: ActiveSubMenu;

  // --- Consent ---
  consentGiven: boolean;
  performanceCookies: boolean;
  marketingCookies: boolean;

  // --- Error ---
  error: AppError | null;

  // --- Loading ---
  loadingProgress: number; // 0–100
  sceneReady: boolean;
  /** ID of the model the user selected in the carousel and pressed “Start”. */
  selectedModelId:
    | string
    | null; /** ID of the currently selected exterior color (from colors.json). null = no color applied yet. */
  selectedColorId: string | null;
  /** ID of the currently selected rim set. null = default not yet applied. */
  selectedRimId: string | null;
  /** ID of the currently selected interior pano variant. null = first variant is active. */
  selectedPanoId: string | null;
  /** True while a rim swap is in progress (loading GLB or waiting for mount). */
  rimLoading: boolean;
  /** Consumption disclaimer of the selected model — shown in the Footer during the viewer screen. */
  consumptionDisclaimer: string | null;
  /** Currently active named scene id (e.g. 'day' | 'night' | 'studio'). Empty string before a scene is loaded. */
  activeScene: string;
  /** Whether the door animation is currently in the open state (doors open). */
  doorsOpen: boolean;
  /** True while an AR session is active (engine should pause). */
  arActive: boolean;

  // --- Actions ---
  setScreen: (screen: AppScreen) => void;
  setCameraMode: (mode: CameraMode) => void;
  setActiveSubMenu: (menu: ActiveSubMenu) => void;
  /**
   * Toggle a submenu slot open/closed. If the given menu is already active,
   * closes it (sets to null). Otherwise opens it (closing any other open menu).
   * This is the primary action for MenuBar buttons.
   */
  toggleSubMenu: (menu: Exclude<ActiveSubMenu, null>) => void;
  giveConsent: (performance: boolean, marketing: boolean) => void;
  reopenConsent: () => void;
  /**
   * Return to the start screen — resets all transient scene state so the
   * engine can be re-initialized cleanly on the next "Start" press.
   * The Viewer component unmounts when screen === 'start', which triggers
   * engine.dispose() in useEngine — this action only handles store state.
   */
  returnToStart: () => void;
  setError: (error: AppError | null) => void;
  setLoadingProgress: (progress: number) => void;
  setSceneReady: (ready: boolean) => void;
  setSelectedModelId: (id: string) => void;
  setConsumptionDisclaimer: (text: string | null) => void;
  setActiveScene: (sceneId: string) => void;
  setSelectedColor: (colorId: string | null) => void;
  setSelectedRim: (rimId: string | null) => void;
  setSelectedPano: (panoId: string | null) => void;
  /** Set rim loading state (true = swap in progress). */
  setRimLoading: (loading: boolean) => void;
  /** Set door open/close state (used by door animation MenuToggleButton). */
  setDoorsOpen: (open: boolean) => void;
  /** Set AR active state (true = AR session running, engine paused). */
  setArActive: (active: boolean) => void;
  /** Set transparent background state (embed only). */
  setTransparentBg: (transparent: boolean) => void;
}

export const useAppStore = create<AppState>((set) => ({
  // Embed mode: skip carousel, load model directly, hide chrome
  embedMode: _isEmbed,
  embedControls: _isEmbed ? _embedControls : 'none',
  arAutoMode: _isArAuto,
  kioskMode: _isKiosk,
  transparentBg: _isTransparentBg,
  // In embed / ar=auto, start on 'loading' directly (no carousel). selectedModelId
  // is set in useConfig from ?model= URL param.
  // Kiosk starts on 'start' so the carousel is shown when multiple models are present;
  // StartScreen auto-starts when only one model is available.
  screen: _isEmbed || _isArAuto ? 'loading' : 'start',
  cameraMode: 'exterior',
  activeSubMenu: null,
  // Hydrate from localStorage — if the user already decided, skip the banner.
  consentGiven: _savedConsent?.given ?? false,
  performanceCookies: _savedConsent?.performance ?? false,
  marketingCookies: _savedConsent?.marketing ?? false,
  error: null,
  loadingProgress: 0,
  sceneReady: false,
  selectedModelId: null,
  selectedColorId: null,
  selectedRimId: null,
  selectedPanoId: null,
  rimLoading: false,
  consumptionDisclaimer: null,
  activeScene: '',
  doorsOpen: false,
  arActive: false,

  setScreen: (screen) => set({ screen }),
  setCameraMode: (cameraMode) => set({ cameraMode, activeSubMenu: null }),
  setActiveSubMenu: (activeSubMenu) => set({ activeSubMenu }),
  toggleSubMenu: (menu) =>
    set((state) => ({
      activeSubMenu: state.activeSubMenu === menu ? null : menu,
    })),
  setLoadingProgress: (loadingProgress) => set({ loadingProgress }),
  setSceneReady: (sceneReady) => set({ sceneReady }),
  setSelectedModelId: (selectedModelId) => set({ selectedModelId }),
  setConsumptionDisclaimer: (consumptionDisclaimer) => set({ consumptionDisclaimer }),
  setActiveScene: (activeScene) => set({ activeScene }),
  setSelectedColor: (selectedColorId) => set({ selectedColorId }),
  setSelectedRim: (selectedRimId) => set({ selectedRimId }),
  setSelectedPano: (selectedPanoId) => set({ selectedPanoId }),
  setRimLoading: (rimLoading) => set({ rimLoading }),
  setDoorsOpen: (doorsOpen) => set({ doorsOpen }),
  setArActive: (arActive) => set({ arActive }),
  setTransparentBg: (transparentBg) => set({ transparentBg }),

  giveConsent: (performance, marketing) => {
    saveConsent({ given: true, performance, marketing });
    set({ consentGiven: true, performanceCookies: performance, marketingCookies: marketing });
  },

  reopenConsent: () => {
    // Clear storage so the banner shows again on next reload too,
    // and the new choice will be saved when the user confirms.
    clearConsent();
    set({ consentGiven: false });
  },

  returnToStart: () =>
    set({
      screen: 'start',
      sceneReady: false,
      loadingProgress: 0,
      selectedModelId: null,
      selectedColorId: null,
      selectedRimId: null,
      selectedPanoId: null,
      rimLoading: false,
      consumptionDisclaimer: null,
      cameraMode: 'exterior',
      activeSubMenu: null,
      error: null,
      activeScene: '',
      doorsOpen: false,
      arActive: false,
    }),

  setError: (error) => set({ error }),
}));
